import React, { useEffect } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import RadioGroup from "../../components/FormikFields/RadioGroup";
import Description from "../../components/Description/Description";
import Modal from "../../components/Modal";
import { TimeInterval } from "../../components/FormikFields";
import { PropTypes } from "prop-types";
import { resourceObjectTypes } from "../../constants";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import LoadingButton from "../../components/Buttons/LoadingButton";
import ButtonGroup from "../../components/Buttons/ButtonGroup";

const getValidationSchema = t => {
  return Yup.object().shape({
    repeatInterval: Yup.number().when("assignmentRecurs", {
      is: "true",
      then: Yup.number().min(1, t("validationMessages.greaterThanZero"))
    })
  });
};

const ConfigureAssignmentForm = ({
  isExistingAssignment,
  assignmentRecurs,
  passingScore,
  duration,
  courseName,
  defaultDaysToComplete,
  defaultRepeatInterval,
  open,
  onClose,
  onSaveAssignment,
  onCancelAssignment,
  isDeleting
}) => {
  const scopedT = useScopedTranslation("administration");
  const { t } = useTranslation();
  const { setFieldValue, isSubmitting } = useFormikContext();

  useEffect(() => {
    if (assignmentRecurs === "true") {
      setFieldValue("repeatInterval", defaultRepeatInterval);
    } else {
      setFieldValue("repeatInterval", 0);
    }
  }, [assignmentRecurs]);

  return (
    <Form>
      <Modal
        showCloseIcon
        maxWidth="sm"
        fullWidth
        open={open}
        onClose={onClose}
        dialogContent={
          <Box display="flex" flexDirection="column">
            <Description label={scopedT("duration")} value={duration} mb={2} />
            <Description
              label={scopedT("passingScore")}
              value={passingScore}
              mb={2}
            />
            <Box mb={3} mt={1.5}>
              <TimeInterval
                name="daysToComplete"
                label={scopedT("timeToComplete")}
                defaultValue={defaultDaysToComplete}
              />
            </Box>
            <RadioGroup
              radioButtons={[
                { label: t("yes"), value: "true" },
                { label: t("no"), value: "false" }
              ]}
              name="assignmentRecurs"
              label={scopedT("assignmentRecursLabel")}
              disabled={isSubmitting}
            />
            <Collapse in={assignmentRecurs === "true"}>
              <Box mt={3}>
                <TimeInterval
                  name="repeatInterval"
                  label={scopedT("repeatInterval")}
                  defaultValue={defaultRepeatInterval}
                />
              </Box>
            </Collapse>
          </Box>
        }
        dialogTitle={
          <Typography variant="h4" color="textPrimary">{`${scopedT(
            isExistingAssignment ? "editAssignment" : "addAssignment"
          )}: ${courseName ? courseName : ""}`}</Typography>
        }
        dialogActions={
          <Box mt={2}>
            <ButtonGroup>
              {isExistingAssignment && (
                <LoadingButton
                  pending={isDeleting}
                  color="primary"
                  variant="outlined"
                  onClick={onCancelAssignment}
                  data-qa="configure-assignment-modal-cancel-assignment-btn"
                >
                  {scopedT("cancelAssignment")}
                </LoadingButton>
              )}
              <LoadingButton
                pending={isSubmitting}
                color="primary"
                variant="contained"
                onClick={onSaveAssignment}
                data-qa="configure-assignment-modal-save-btn"
              >
                {scopedT(
                  isExistingAssignment ? "saveAssignment" : "addAssignment"
                )}
              </LoadingButton>
            </ButtonGroup>
          </Box>
        }
        data-qa="configure-assignment-modal"
      />
    </Form>
  );
};

const ConfigureAssignmentModal = ({
  assignmentId,
  isExistingAssignment,
  daysToComplete,
  repeatInterval,
  trainingObjectId,
  referenceType,
  referenceId,
  courseName,
  passingScore,
  duration,
  onClose,
  open,
  onSaveAssignment,
  onCancelAssignment,
  isDeleting
}) => {
  const { t } = useTranslation();
  return (
    <Formik
      initialValues={{
        id: assignmentId,
        daysToComplete,
        assignmentRecurs: repeatInterval > 0 ? "true" : "false",
        repeatInterval,
        trainingObject: {
          type: resourceObjectTypes.trainingObject,
          id: `${trainingObjectId}`
        },
        reference: {
          type: referenceType,
          id: `${referenceId}`
        }
      }}
      onSubmit={async values => onSaveAssignment(values)}
      enableReinitialize
      validationSchema={getValidationSchema(t)}
      validateOnChange={false}
    >
      {({ submitForm, isSubmitting, values }) => (
        <ConfigureAssignmentForm
          assignmentRecurs={values.assignmentRecurs}
          isSubmitting={isSubmitting}
          open={open}
          onClose={onClose}
          courseName={courseName}
          passingScore={passingScore}
          duration={duration}
          defaultDaysToComplete={daysToComplete}
          defaultRepeatInterval={!!repeatInterval ? repeatInterval : 365}
          onSaveAssignment={submitForm}
          onCancelAssignment={onCancelAssignment}
          isExistingAssignment={isExistingAssignment}
          isDeleting={isDeleting}
        />
      )}
    </Formik>
  );
};

export default ConfigureAssignmentModal;

ConfigureAssignmentModal.propTypes = {
  referenceType: PropTypes.oneOf(["location", "position", "group"]),
  referenceId: PropTypes.string
};
